import React from "react";
import { Loader2, Plus, Trash2, Pencil, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, authHeaders, apiError } from "@/lib/api";

const field =
  "w-full rounded-lg border border-white/12 bg-[#0B0F1A] px-3.5 py-2.5 text-sm text-white outline-none transition-colors duration-200 placeholder:text-[#5D677F] focus:border-[#8E9AEA]";
const label = "text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  metaTitle: "",
  metaDescription: "",
  keywords: "",
  coverImage: "",
  tags: "",
  status: "published",
  faqs: [],
};

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 90);

const Counter = ({ value, max }) => (
  <span className={`ml-2 text-[10px] font-semibold ${value > max ? "text-[#F2707A]" : "text-[#5D677F]"}`}>
    {value}/{max}
  </span>
);

export const PostsPanel = () => {
  const [posts, setPosts] = React.useState([]);
  const [form, setForm] = React.useState(EMPTY);
  const [editingId, setEditingId] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/posts", { headers: authHeaders() });
      setPosts(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    setOpen(true);
  };

  const startEdit = (p) => {
    setForm({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt || "",
      body: p.body || "",
      metaTitle: p.metaTitle || "",
      metaDescription: p.metaDescription || "",
      keywords: p.keywords || "",
      coverImage: p.coverImage || "",
      tags: (p.tags || []).join(", "),
      status: p.status,
      faqs: p.faqs || [],
    });
    setEditingId(p.id);
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, slug: slugify(form.slug || form.title) };
    try {
      if (editingId) {
        await api.put(`/admin/posts/${editingId}`, payload, { headers: authHeaders() });
        toast.success("Article updated");
      } else {
        await api.post("/admin/posts", payload, { headers: authHeaders() });
        toast.success("Article published");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`, { headers: authHeaders() });
      toast.success("Article deleted");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setFaq = (i, k, v) =>
    setForm((f) => ({ ...f, faqs: f.faqs.map((q, j) => (i === j ? { ...q, [k]: v } : q)) }));

  return (
    <div className="mt-8" data-testid="admin-posts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-white">Blog articles ({posts.length})</h2>
          <p className="mt-1 text-xs text-[#A6B0C9]">
            Published articles appear on /insights and in the Resources page blog list, with Article and FAQ schema
            generated automatically.
          </p>
        </div>
        <button onClick={startNew} className="btn-primary !px-4 !py-2.5 !text-sm" data-testid="admin-new-post-btn">
          <Plus className="h-4 w-4" /> New article
        </button>
      </div>

      {open && (
        <form
          onSubmit={submit}
          className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
          data-testid="admin-post-form"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-base font-bold text-white">
              {editingId ? "Edit article" : "New article"}
            </h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[#A6B0C9] transition-colors duration-200 hover:text-white"
              data-testid="admin-post-form-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <p className={label}>Title</p>
              <input
                value={form.title}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, title: v, slug: editingId ? f.slug : slugify(v) }));
                }}
                required
                className={`${field} mt-2`}
                placeholder="CERT-In 6-hour reporting explained"
                data-testid="admin-post-title"
              />
            </div>
            <div>
              <p className={label}>URL slug</p>
              <input
                value={form.slug}
                onChange={set("slug")}
                className={`${field} mt-2`}
                placeholder="cert-in-6-hour-reporting"
                data-testid="admin-post-slug"
              />
              <p className="mt-1.5 text-[11px] text-[#5D677F]">/insights/{slugify(form.slug || form.title) || "…"}</p>
            </div>
            <div>
              <p className={label}>Status</p>
              <select value={form.status} onChange={set("status")} className={`${field} mt-2`} data-testid="admin-post-status">
                <option value="published">Published</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <p className={label}>
                Excerpt / summary
                <Counter value={form.excerpt.length} max={220} />
              </p>
              <textarea
                value={form.excerpt}
                onChange={set("excerpt")}
                rows={2}
                className={`${field} mt-2 resize-y`}
                placeholder="One or two sentences shown on the insights list."
                data-testid="admin-post-excerpt"
              />
            </div>
            <div>
              <p className={label}>
                Meta title (SEO)
                <Counter value={form.metaTitle.length} max={60} />
              </p>
              <input
                value={form.metaTitle}
                onChange={set("metaTitle")}
                className={`${field} mt-2`}
                placeholder="Leave blank to use the title"
                data-testid="admin-post-metatitle"
              />
            </div>
            <div>
              <p className={label}>Tags (comma separated)</p>
              <input
                value={form.tags}
                onChange={set("tags")}
                className={`${field} mt-2`}
                placeholder="CERT-In, Compliance"
                data-testid="admin-post-tags"
              />
            </div>
            <div className="sm:col-span-2">
              <p className={label}>
                Meta description (SEO)
                <Counter value={form.metaDescription.length} max={155} />
              </p>
              <textarea
                value={form.metaDescription}
                onChange={set("metaDescription")}
                rows={2}
                className={`${field} mt-2 resize-y`}
                placeholder="Shown in Google results. Under 155 characters."
                data-testid="admin-post-metadescription"
              />
            </div>
            <div className="sm:col-span-2">
              <p className={label}>Keywords (comma separated)</p>
              <input
                value={form.keywords}
                onChange={set("keywords")}
                className={`${field} mt-2`}
                data-testid="admin-post-keywords"
              />
            </div>
            <div className="sm:col-span-2">
              <p className={label}>Body (markdown: ## heading, **bold**, - list, | tables |)</p>
              <textarea
                value={form.body}
                onChange={set("body")}
                rows={14}
                className={`${field} mt-2 resize-y font-mono text-[13px] leading-relaxed`}
                placeholder={"Intro paragraph.\n\n## First section\n\n- point one\n- point two"}
                data-testid="admin-post-body"
              />
            </div>
          </div>

          <div className="mt-6 border-t border-white/10 pt-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className={label}>FAQ entries (generate FAQ rich results)</p>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, faqs: [...f.faqs, { q: "", a: "" }] }))}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-1.5 text-xs font-semibold text-[#DDE3F2] transition-colors duration-200 hover:border-white/30 hover:text-white"
                data-testid="admin-post-add-faq"
              >
                <Plus className="h-3.5 w-3.5" /> Add question
              </button>
            </div>
            <div className="mt-4 flex flex-col gap-3">
              {form.faqs.map((f, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-[#0B0F1A]/60 p-3.5" data-testid={`admin-post-faq-${i}`}>
                  <div className="flex items-start gap-2">
                    <input
                      value={f.q}
                      onChange={(e) => setFaq(i, "q", e.target.value)}
                      placeholder="Question"
                      className={field}
                      data-testid={`admin-post-faq-q-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, faqs: s.faqs.filter((_, j) => j !== i) }))}
                      className="mt-2 shrink-0 text-[#A6B0C9] transition-colors duration-200 hover:text-[#F2707A]"
                      data-testid={`admin-post-faq-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <textarea
                    value={f.a}
                    onChange={(e) => setFaq(i, "a", e.target.value)}
                    rows={2}
                    placeholder="Answer"
                    className={`${field} mt-2 resize-y`}
                    data-testid={`admin-post-faq-a-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button type="submit" disabled={busy} className="btn-primary !px-5 !py-2.5 !text-sm" data-testid="admin-post-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {editingId ? "Save changes" : "Publish article"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="btn-secondary !px-5 !py-2.5 !text-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 flex flex-col gap-3" data-testid="admin-posts-list">
        {posts.length === 0 && (
          <p className="text-sm text-[#A6B0C9]" data-testid="admin-posts-empty">
            No articles yet. Create your first one.
          </p>
        )}
        {posts.map((p) => (
          <div
            key={p.id}
            className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`admin-post-row-${p.slug}`}
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                    p.status === "published"
                      ? "bg-[#7BE0B0]/15 text-[#7BE0B0]"
                      : "bg-[#E7C46A]/15 text-[#E7C46A]"
                  }`}
                >
                  {p.status}
                </span>
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#A6B0C9]">
                  {new Date(p.publishedAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}
                </span>
                <span className="text-[11px] text-[#5D677F]">{p.readingMinutes} min</span>
              </div>
              <p className="mt-1.5 truncate font-semibold text-white">{p.title}</p>
              <p className="truncate text-xs text-[#5D677F]">/insights/{p.slug}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <a
                href={`/insights/${p.slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-2 text-xs font-semibold text-[#DDE3F2] transition-colors duration-200 hover:border-white/30 hover:text-white"
                data-testid={`admin-post-view-${p.slug}`}
              >
                <Eye className="h-3.5 w-3.5" /> View
              </a>
              <button
                onClick={() => startEdit(p)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-2 text-xs font-semibold text-[#DDE3F2] transition-colors duration-200 hover:border-white/30 hover:text-white"
                data-testid={`admin-post-edit-${p.slug}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                onClick={() => remove(p)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#F2707A]/30 px-3 py-2 text-xs font-semibold text-[#F2707A] transition-colors duration-200 hover:bg-[#F2707A]/10"
                data-testid={`admin-post-delete-${p.slug}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
